import { CircleAlert, RefreshCw, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from './Button'
import { cx } from './cx'

type EmptyStateProps = {
  icon?: LucideIcon
  /** What is missing, as a sentence ("Noch keine Trainings in diesem Monat."). */
  title: ReactNode
  text?: ReactNode
  /** The action that fixes it. */
  action?: ReactNode
  /** Less vertical room, for use inside tables and cards. */
  compact?: boolean
}

export function EmptyState({ icon: Icon, title, text, action, compact }: EmptyStateProps) {
  return (
    <div className={cx('flex flex-col items-center text-center', compact ? 'px-4 py-10' : 'px-6 py-16 md:py-20')}>
      {Icon && (
        <span className="mb-4 grid size-11 place-items-center rounded-full bg-bg-subtle text-ink-3 ring-1 ring-line">
          <Icon aria-hidden="true" className="size-5" strokeWidth={1.75} />
        </span>
      )}
      <p className="text-body font-medium text-ink">{title}</p>
      {text && <p className="mt-1 max-w-sm text-small text-ink-2 text-pretty">{text}</p>}
      {action && <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}

type ErrorStateProps = {
  /** Short German sentence; never raw server text. */
  message?: ReactNode
  title?: ReactNode
  onRetry?: () => void
  retrying?: boolean
  compact?: boolean
}

export function ErrorState({
  title = 'Das hat nicht geklappt.',
  message = 'Die Daten konnten nicht geladen werden. Prüf deine Verbindung und versuch es erneut.',
  onRetry,
  retrying,
  compact,
}: ErrorStateProps) {
  return (
    <div role="alert" className={cx('flex flex-col items-center text-center', compact ? 'px-4 py-10' : 'px-6 py-16 md:py-20')}>
      <span className="mb-4 grid size-11 place-items-center rounded-full bg-negative-soft text-negative">
        <CircleAlert aria-hidden="true" className="size-5" strokeWidth={1.75} />
      </span>
      <p className="text-body font-medium text-ink">{title}</p>
      <p className="mt-1 max-w-sm text-small text-ink-2 text-pretty">{message}</p>
      {onRetry && (
        <Button className="mt-5" icon={RefreshCw} loading={retrying} onClick={onRetry}>
          Erneut versuchen
        </Button>
      )}
    </div>
  )
}

type SkeletonProps = {
  variant?: 'block' | 'text' | 'circle'
  /** Sizing utilities, e.g. "h-24 w-full" or "w-32". Skeletons must match the final size exactly. */
  className?: string
}

/** Placeholder in the exact final size. Decorative — wrap groups in SkeletonGroup for the busy state. */
export function Skeleton({ variant = 'block', className }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'block animate-pulse bg-bg-hover',
        variant === 'circle' ? 'rounded-full' : variant === 'text' ? 'h-[0.8em] rounded-[4px]' : 'rounded-control',
        className,
      )}
    />
  )
}

/** aria-busy wrapper: announces loading once, hides the placeholder shapes. */
export function SkeletonGroup({ label = 'Wird geladen …', children, className }: { label?: string; children: ReactNode; className?: string }) {
  return (
    <div aria-busy="true" className={className}>
      <output className="sr-only">{label}</output>
      {children}
    </div>
  )
}
